export type SoundName = 'fire' | 'fishHit' | 'coinCollect' | 'bossHit' | 'sessionComplete' | 'gameOver';

interface ToneDefinition {
  frequency: number;
  endFrequency: number;
  duration: number;
  volume: number;
  type: OscillatorType;
}

const TONES: Record<SoundName, ToneDefinition> = {
  fire: { frequency: 210, endFrequency: 95, duration: 0.11, volume: 0.035, type: 'sine' },
  fishHit: { frequency: 300, endFrequency: 190, duration: 0.08, volume: 0.03, type: 'triangle' },
  coinCollect: { frequency: 520, endFrequency: 920, duration: 0.16, volume: 0.035, type: 'sine' },
  bossHit: { frequency: 130, endFrequency: 55, duration: 0.28, volume: 0.055, type: 'sawtooth' },
  sessionComplete: { frequency: 440, endFrequency: 880, duration: 0.48, volume: 0.04, type: 'triangle' },
  gameOver: { frequency: 260, endFrequency: 70, duration: 0.5, volume: 0.04, type: 'sawtooth' },
};

let muted = false;
let audioContext: AudioContext | null = null;
let ambientOscillator: OscillatorNode | null = null;
let ambientGain: GainNode | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioContextConstructor = window.AudioContext;
  if (!AudioContextConstructor) return null;
  audioContext ??= new AudioContextConstructor();
  if (audioContext.state === 'suspended') void audioContext.resume().catch(() => undefined);
  return audioContext;
}

function synth(name: SoundName) {
  const context = getAudioContext();
  const tone = TONES[name];
  if (!context) return;

  const now = context.currentTime;
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = tone.type;
  oscillator.frequency.setValueAtTime(tone.frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, tone.endFrequency), now + tone.duration);
  gain.gain.setValueAtTime(tone.volume, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.duration);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + tone.duration + 0.02);
  oscillator.onended = () => {
    oscillator.disconnect();
    gain.disconnect();
  };
}

export function setMuted(value: boolean) {
  muted = value;
  if (muted) void getAudioContext()?.suspend().catch(() => undefined);
  else void getAudioContext()?.resume().catch(() => undefined);
}

export function isMuted() {
  return muted;
}

export function playSound(name: SoundName) {
  if (!muted) synth(name);
}

export function playFire() {
  playSound('fire');
}

export function playHit() {
  playSound('fishHit');
}

export function playFishDeath() {
  playSound('coinCollect');
}

export function playBossDeath() {
  playSound('sessionComplete');
}

export function startAmbient() {
  if (muted || ambientOscillator) return;
  const context = getAudioContext();
  if (!context) return;

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(58, context.currentTime);
  gain.gain.setValueAtTime(0.008, context.currentTime);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  ambientOscillator = oscillator;
  ambientGain = gain;
}

export function stopAmbient() {
  if (!ambientOscillator || !ambientGain) return;
  ambientOscillator.stop();
  ambientOscillator.disconnect();
  ambientGain.disconnect();
  ambientOscillator = null;
  ambientGain = null;
}
